"use client";

import { auth, db } from "@/lib/firebase-client";
import type { UserProfileDocument } from "@/lib/types";
import type { User } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";

type ProfileState = {
  user: User | null;
  profile: (UserProfileDocument & { id: string }) | null;
  loading: boolean;
};

export function useAuthProfile(): ProfileState {
  const [state, setState] = useState<ProfileState>({
    user: null,
    profile: null,
    loading: true,
  });

  useEffect(() => {
    let unsubscribeProfile: (() => void) | undefined;

    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = undefined;
      }

      if (firebaseUser) {
        const profileRef = doc(db, "users", firebaseUser.uid);
        unsubscribeProfile = onSnapshot(
          profileRef,
          (snapshot) => {
            if (snapshot.exists()) {
              setState({
                user: firebaseUser,
                profile: {
                  id: snapshot.id,
                  ...(snapshot.data() as UserProfileDocument),
                },
                loading: false,
              });
            } else {
              setState({ user: firebaseUser, profile: null, loading: false });
            }
          },
          (error) => {
            console.error("Failed to load profile", error);
            setState({ user: firebaseUser, profile: null, loading: false });
          },
        );
      } else {
        setState({ user: null, profile: null, loading: false });
      }
    });

    return () => {
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
      unsubscribeAuth();
    };
  }, []);

  return state;
}
